import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "../atoms/Link";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";

export interface BreadcrumbItem {
	label: string;
	href?: string;
}

export interface BreadcrumbTrailProps {
	items: BreadcrumbItem[];
	className?: string;
}

function BreadcrumbTrail({
	items,
	className,
}: BreadcrumbTrailProps): ReactNode {
	if (items.length === 0) {
		return null;
	}

	return (
		<nav
			aria-label="Breadcrumb"
			className={cn("flex items-center gap-1", className)}
		>
			<ol className="flex flex-wrap items-center gap-1">
				{items.map((item, idx) => {
					const isLast = idx === items.length - 1;
					return (
						// biome-ignore lint/suspicious/noArrayIndexKey: breadcrumbs are static ordered items; label alone may not be unique
						<li key={idx} className="flex items-center gap-1">
							{idx > 0 && (
								<ChevronRight
									className="h-3 w-3 shrink-0 text-mutedForeground"
									aria-hidden="true"
								/>
							)}
							{isLast || !item.href ? (
								<Text
									size="sm"
									textColor={isLast ? "foreground" : "mutedForeground"}
									as="span"
									aria-current={isLast ? "page" : undefined}
								>
									{item.label}
								</Text>
							) : (
								<Link href={item.href} className="text-sm text-mutedForeground">
									{item.label}
								</Link>
							)}
						</li>
					);
				})}
			</ol>
		</nav>
	);
}
BreadcrumbTrail.displayName = "BreadcrumbTrail";

export default BreadcrumbTrail;
export { BreadcrumbTrail };
